import { useState, useEffect } from 'react'
import { cn } from '@/utils/cn'

interface PreferencesData {
  aiUsage?: string
  preferredTopics?: string[]
  dataSharingConsent?: string
  observations?: string
}

interface PreferencesStepProps {
  data?: Partial<PreferencesData>
  onChange: (data: Partial<PreferencesData>) => void
  onValidationChange: (isValid: boolean) => void
}

const aiUsageOptions = [
  { id: 'never', label: 'Nunca usei' },
  { id: 'sometimes', label: 'Uso às vezes' },
  { id: 'frequently', label: 'Uso com frequência' }
]

const preferredTopics = [
  'Solo', 'Clima', 'Bioinsumos', 'Nutrição de plantas', 'Manejo de pragas',
  'Pecuária', 'Transição regenerativa', 'Planejamento produtivo', 'Mercado', 'Outro'
]

const dataSharingOptions = [
  { id: 'yes', label: 'Sim', description: 'Ajude a gerar inteligência coletiva anonimamente' },
  { id: 'no', label: 'Não', description: 'Prefiro manter meus dados privados' },
  { id: 'understand', label: 'Quero entender melhor', description: 'Gostaria de mais informações antes de decidir' }
]

export function PreferencesStep({ data, onChange, onValidationChange }: PreferencesStepProps) {
  const [selectedTopics, setSelectedTopics] = useState<string[]>(data?.preferredTopics || [])

  // Sempre válido (campos opcionais)
  useEffect(() => {
    onValidationChange(true)
  }, [])

  const handleTopicChange = (topic: string) => {
    const newTopics = selectedTopics.includes(topic)
      ? selectedTopics.filter(t => t !== topic)
      : [...selectedTopics, topic]

    setSelectedTopics(newTopics)
    onChange({ preferredTopics: newTopics })
  }

  return (
    <div className="space-y-8">

      {/* Uso de IA */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-brand-text">
          Você já utiliza inteligência artificial no seu dia a dia?
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {aiUsageOptions.map(option => (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange({ aiUsage: option.id })}
              className={cn(
                "p-4 rounded-xl border-2 transition-all text-left",
                "hover:shadow-lg hover:scale-[1.02]",
                data?.aiUsage === option.id
                  ? "border-brand-primary bg-brand-primary/10 shadow-lg ring-2 ring-brand-primary/20"
                  : "border-brand-border bg-brand-surface hover:border-brand-primary/50"
              )}
            >
              <span className="text-brand-text font-medium">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Temas de interesse */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-brand-text">
          Quais temas você mais quer consultar no RegenerAI?
        </h3>
        <p className="text-brand-text-secondary text-sm">
          Marque uma ou mais opções de seu interesse
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {preferredTopics.map(topic => (
            <button
              key={topic}
              type="button"
              onClick={() => handleTopicChange(topic)}
              className={cn(
                "p-3 rounded-lg border text-sm transition-all",
                selectedTopics.includes(topic)
                  ? "border-brand-primary bg-brand-primary/10 shadow-md ring-1 ring-brand-primary/20"
                  : "border-brand-border bg-brand-surface hover:border-brand-primary/50"
              )}
            >
              {topic}
            </button>
          ))}
        </div>
      </div>

      {/* Consentimento de dados */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-brand-text">
          Você aceita compartilhar dados anonimizados para gerar inteligência coletiva?
        </h3>
        <p className="text-brand-text-secondary text-sm">
          Seus dados serão sempre anonimizados e usados apenas para melhorar as recomendações do sistema
        </p>
        <div className="space-y-3">
          {dataSharingOptions.map(option => (
            <button
              key={option.id}
              type="button"
              onClick={() => onChange({ dataSharingConsent: option.id })}
              className={cn(
                "w-full p-4 rounded-xl border-2 transition-all text-left",
                "hover:shadow-lg",
                data?.dataSharingConsent === option.id
                  ? "border-brand-primary bg-brand-primary/5 shadow-lg"
                  : "border-brand-border bg-brand-surface hover:border-brand-primary/50"
              )}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-brand-text mb-1">
                    {option.label}
                  </div>
                  <div className="text-sm text-brand-text-secondary">
                    {option.description}
                  </div>
                </div>
                <div
                  className={cn(
                    "w-5 h-5 rounded-full border-2 transition-colors",
                    data?.dataSharingConsent === option.id
                      ? "border-brand-primary bg-brand-primary"
                      : "border-brand-border"
                  )}
                >
                  {data?.dataSharingConsent === option.id && (
                    <div className="w-full h-full rounded-full bg-white scale-[0.4]" />
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Observações */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-brand-text">
          Há algo mais que você gostaria de informar?
        </h3>
        <p className="text-brand-text-secondary text-sm mb-3">
          Use este campo para adicionar qualquer informação relevante sobre sua realidade, desafios ou interesses.
        </p>
        <textarea
          value={data?.observations || ''}
          onChange={(e) => onChange({ observations: e.target.value })}
          className="input-field min-h-[120px] resize-none"
          placeholder="Descreva aqui qualquer informação adicional que julgar importante..."
          maxLength={1000}
        />
        <p className="text-xs text-brand-text-muted text-right">
          {(data?.observations?.length || 0)} / 1000 caracteres
        </p>
      </div>
    </div>
  )
}
